export function validateCandidateRoot(
  root,
  candidateTypes,
  actionTypes,
  invalidCharacters,
  shows,
  isInitialLoad = false,
) {
  const noneType = findValueByLabel(actionTypes, "None");
  const archiveType = findValueByLabel(actionTypes, "Archive");

  const movieType = findValueByLabel(candidateTypes, "Movie");
  const isMovieRoot = root.archiveCandidateType === movieType;

  if (isMovieRoot) {
    validateMovieRoot(root, invalidCharacters, archiveType, noneType, isInitialLoad);
  } else {
    validateSeriesRoot(root, invalidCharacters, shows, archiveType, noneType, isInitialLoad);
  }

  return root;
}

/* --------------------------------- helpers -------------------------------- */

function findValueByLabel(items, label) {
  return items.find((x) => x.label === label)?.value;
}

function hasInvalidCharacters(name, invalidCharacters) {
  return invalidCharacters.some((ch) => name.includes(ch));
}

/**
 * Keeps user-selected action types intact unless the action is "auto-managed"
 * (archive/none/null/undefined). Then updates based on validity transitions.
 */
function nextActionType(currentActionType, wasValid, isValidNow, archiveType, noneType) {
  const isAutoManaged = [archiveType, noneType, null, undefined].includes(currentActionType);
  if (!isAutoManaged) return currentActionType;

  // first computed validity
  if (wasValid === undefined) return isValidNow ? archiveType : noneType;

  // validity changed
  if (wasValid !== isValidNow) return isValidNow ? archiveType : noneType;

  // no change
  return currentActionType;
}

/**
 * IMPORTANT UI RULE:
 * - Validation may FORCE edit ON (when invalid & user-fixable),
 * - Validation must NEVER force edit OFF (so manual edit stays open).
 */
function forceEditOnIfNeeded(entity, shouldForceOn) {
  if (shouldForceOn) {
    entity.isEditState = true;
  }
}

/* ---------------------------------- movie --------------------------------- */

function validateMovieRoot(root, invalidCharacters, archiveType, noneType, isInitialLoad) {
  const candidates = root.archiveCandidates ?? [];

  if (isInitialLoad) {
    root.isExpanded = candidates.length === 1;
  }

  if (candidates.length === 1) {
    const c = candidates[0];
    c.isValid = isMovieCandidateValid(c, invalidCharacters);

    // Only force ON when invalid (user-fixable). Never force OFF.
    forceEditOnIfNeeded(c, !c.isValid);
  } else {
    const anyNameIsSet = candidates.some((c) => (c.name ?? "").length > 0);

    candidates.forEach((c) => {
      c.isValid = isMovieCandidateValid(c, invalidCharacters, anyNameIsSet);
      forceEditOnIfNeeded(c, !c.isValid);
    });
  }

  const rootIsValid = candidates.every((c) => c.isValid);

  root.archiveActionType = nextActionType(
    root.archiveActionType,
    root.isValid,
    rootIsValid,
    archiveType,
    noneType,
  );

  root.isValid = rootIsValid;
}

function isMovieCandidateValid(candidate, invalidCharacters, anyNameIsSet = false) {
  const name = candidate.name ?? "";
  const hasNameOrSiblingHasName = name.length > 0 || anyNameIsSet;
  if (!hasNameOrSiblingHasName) return false;
  return !hasInvalidCharacters(name, invalidCharacters);
}

/* --------------------------------- series --------------------------------- */

function validateSeriesRoot(root, invalidCharacters, shows, archiveType, noneType, isInitialLoad) {
  const candidates = root.archiveCandidates ?? [];

  if (isInitialLoad) {
    root.isExpanded = candidates.length === 1;
  }

  if (candidates.length === 1) {
    const { isValid, hasUserFixableError } = validateSeriesCandidateDetailed(
      candidates[0],
      shows,
      invalidCharacters,
    );

    root.archiveActionType = nextActionType(
      root.archiveActionType,
      root.isValid,
      isValid,
      archiveType,
      noneType,
    );

    root.isValid = isValid;

    // Force edit ON only if invalid AND fixable (not already-exists-only).
    forceEditOnIfNeeded(root, !isValid && hasUserFixableError);
    return;
  }

  candidates.forEach((c) => {
    const { isValid, hasUserFixableError } = validateSeriesCandidateDetailed(
      c,
      shows,
      invalidCharacters,
    );

    c.isValid = isValid;

    // Force edit ON only if invalid AND fixable.
    // If the ONLY issue is "already exists", hasUserFixableError is false -> no forced edit.
    // If "already exists" + invalid chars (or other fixable), hasUserFixableError true -> forced edit.
    forceEditOnIfNeeded(c, !isValid && hasUserFixableError);

    c.archiveActionType = nextActionType(
      c.archiveActionType,
      c.isValid,
      isValid,
      archiveType,
      noneType,
    );
  });

  root.isValid = candidates.every((c) => c.isValid);
}

function validateSeriesCandidateDetailed(candidate, shows, invalidCharacters) {
  // Missing identifiers are user-fixable -> edit should be possible
  if (candidate.seriesId == null || candidate.seasonNumber == null) {
    return {
      isValid: false,
      hasAlreadyExistsConflict: false,
      hasUserFixableError: true,
    };
  }

  const show = shows.find((s) => s.id === candidate.seriesId);
  if (!show) {
    return {
      isValid: false,
      hasAlreadyExistsConflict: false,
      hasUserFixableError: true,
    };
  }

  const episodes = [
    {
      episodeNumber: candidate.episodeNumber,
      name: candidate.name ?? "",
      episodeAlreadyExists: candidate.episodeAlreadyExists ?? false,
    },
    ...(candidate.additionalEpisodes ?? []),
  ];

  let hasAlreadyExistsConflict = false;
  let hasUserFixableError = false;

  // ALL episodes must be valid
  const allValid = episodes.every((e) => {
    const result = validateEpisodeDetailed(e, show.episodeNameRequired, invalidCharacters);
    if (result.hasAlreadyExistsConflict) hasAlreadyExistsConflict = true;
    if (result.hasUserFixableError) hasUserFixableError = true;
    return result.isValid;
  });

  return { isValid: allValid, hasAlreadyExistsConflict, hasUserFixableError };
}

function validateEpisodeDetailed(episode, episodeNameRequired, invalidCharacters) {
  let hasAlreadyExistsConflict = false;
  let hasUserFixableError = false;

  const name = episode.name ?? "";

  if (episode.episodeAlreadyExists) hasAlreadyExistsConflict = true;
  if (!(episode.episodeNumber >= 0)) hasUserFixableError = true;
  if (episodeNameRequired && name.length === 0) hasUserFixableError = true;
  if (hasInvalidCharacters(name, invalidCharacters)) hasUserFixableError = true;

  const isValid = !hasAlreadyExistsConflict && !hasUserFixableError;
  return { isValid, hasAlreadyExistsConflict, hasUserFixableError };
}
